/**
 * logUploadEvent.jsx
 * 
 * Fire-and-forget audit logger for the receipt upload flow.
 * 
 * Calls POST /log-upload-event on spikeAPIRouterService.
 * NEVER throws — a logging failure must never interrupt an upload.
 * 
 * Usage:
 *   import { logUploadEvent } from '../functions/logUploadEvent.jsx';
 *   await logUploadEvent('S3_UPLOAD_STARTED', { fileName: file.name, fileSize: file.size });
 */

import { fetchAuthSession } from 'aws-amplify/auth';

export const logUploadEvent = async (step, payload = {}) => {

  const API_ENDPOINT = `${import.meta.env.VITE_API_BASE_URL}/log-upload-event`;

  try {
    // Reuse the same Cognito JWT auth pattern as the rest of your API calls
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();

    if (!token) {
      // Can't log without a token — bail silently
      console.warn('[logUploadEvent] No auth token available, skipping log step:', step);
      return;
    }

    const body = JSON.stringify({
      step,
      timestamp: new Date().toISOString(),
      ...payload,
    });

    // Best-effort POST — no await on the response, let it fly
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body,
      // keepalive: true allows the request to outlive the page if the user
      // navigates away — important for logging the final UPLOAD_COMPLETE step
      keepalive: true,
    }).catch((fetchErr) => {
      console.warn('[logUploadEvent] Fetch failed (non-fatal):', fetchErr.message);
    });

  } catch (err) {
    // Swallow all errors — logging must never break the upload
    console.warn('[logUploadEvent] Unexpected error (non-fatal):', err.message);
  }
};
